import { MutationCache, QueryCache, type Mutation } from "@tanstack/react-query";
import { ApiError } from "../api/apiTypes";
import { getTelemetryRoute, startUserAction, trackEvent } from "./telemetry";

const CRITICAL_ACTION = /^(payment|subscription)\./;
const statusOf = (error: unknown) => (error instanceof ApiError ? error.Status : undefined);
const durationOf = (mutation: Mutation<unknown, unknown, unknown>) =>
  mutation.state.submittedAt ? Date.now() - mutation.state.submittedAt : undefined;
const idOf = (data: unknown) =>
  data && typeof data === "object" && "id" in data && typeof data.id === "string" ? data.id : undefined;
// Leading static parts such as ["client", "list"]; IDs and filters are left out.
const queryName = (key: readonly unknown[]) => {
  const parts: string[] = [];
  for (const part of key.slice(0, 2)) {
    if (typeof part !== "string" || !/^[A-Za-z_-]+$/.test(part)) break;
    parts.push(part);
  }
  return parts.join(".") || "unknown";
};

/** Query client caches that report tagged key actions (`meta.action`) as events and Faro user actions, plus failed data loads. */
export function createTelemetryCaches() {
  return {
    mutationCache: new MutationCache({
      onMutate(_variables, mutation) {
        const action = mutation.meta?.action;
        if (!action) return;
        startUserAction(action, { trigger: "mutation", critical: CRITICAL_ACTION.test(action) });
      },
      onSuccess(data, _variables, _result, mutation) {
        const action = mutation.meta?.action;
        if (!action) return;
        trackEvent("action.succeeded", {
          action, route: getTelemetryRoute(), entityId: idOf(data), durationMs: durationOf(mutation),
        });
      },
      onError(error, _variables, _result, mutation) {
        const action = mutation.meta?.action;
        if (!action) return;
        trackEvent("action.failed", {
          action, route: getTelemetryRoute(), status: statusOf(error), durationMs: durationOf(mutation),
        });
      },
    }),
    queryCache: new QueryCache({
      onError(error, query) {
        trackEvent("data.load_failed", {
          query: queryName(query.queryKey), route: getTelemetryRoute(), status: statusOf(error),
        });
      },
    }),
  };
}
